import _AccountHistoryQuery from "../__generated__/AccountHistoryQuery.graphql";
import { useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Navigate } from 'react-router-dom';
import { En } from '../i18n/En';
import { T } from '../i18n/RichText';
import { t } from '../i18n/t';
import { getLocale, langOf } from '../i18n/locale';
import { registerFor } from '../profile/register';
import { BooksRead } from './BooksRead.tsx';
import { suggestions, thisWeek, topicCounts, WEEK_DAYS, weekDays, type Suggestion } from './familyWeek.ts';
import { HoldGate } from './HoldGate.tsx';
import { MemoriesCard } from './MemoriesCard.tsx';
import { HearMyself } from './ScrapbookView.tsx';
import { SessionList } from './SessionList.tsx';
import { KID_GAMES } from './topics.english.ts';
import { topicLabel } from './topics.ts';
import type { AccountHistoryQuery } from '../__generated__/AccountHistoryQuery.graphql';
import type { PlainKey } from '../i18n/t';

/**
 * `first: 20`, matching `ProgressViewQuery`: the two screens list the same
 * connection, so an identical `first` means one Relay storage key.
 */
const AccountHistoryQuery = (_AccountHistoryQuery.hash && _AccountHistoryQuery.hash !== "8fbd00c26067c0580c08426384bfa577" && console.error("The definition of 'AccountHistoryQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AccountHistoryQuery);
const SENTENCES_SHOWN = 3;
const GAME_TRY: Record<string, PlainKey> = {
  [KID_GAMES[0]]: 'grown-ups-try-riddles',
  [KID_GAMES[1]]: 'grown-ups-try-guess',
  [KID_GAMES[2]]: 'grown-ups-try-teach',
  [KID_GAMES[3]]: 'grown-ups-try-story',
  [KID_GAMES[4]]: 'grown-ups-try-spy',
  [KID_GAMES[5]]: 'grown-ups-try-rather',
  [KID_GAMES[6]]: 'grown-ups-try-rhyme'
};
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="flex flex-col gap-2">
      <h3 className="font-semibold">{title}</h3>
      {children}
    </section>;
}
function SuggestionLine({
  s,
  name
}: {
  s: Suggestion;
  name: string | null;
}) {
  switch (s.kind) {
    case 'none':
      return <>{t('grown-ups-try-none')}</>;
    case 'book':
      return <T k="grown-ups-try-book" params={{
        title: s.title,
        ask: s.ask
      }} parts={{
        en: x => <En>{x}</En>
      }} />;
    case 'game':
      return <>{t(GAME_TRY[s.topic] ?? 'grown-ups-try-riddles')}</>;
    case 'topic':
      return <>{t('grown-ups-try-topic', {
          topic: topicLabel(s.topic, name)
        })}</>;
  }
}

/**
 * The kid account's page for the grown-ups (kid engagement PRD F2): the last
 * seven days, a few ideas for joining in, what Cappy remembers, and every past
 * conversation with its debrief, behind a press-and-hold. Kid debriefs name no
 * faults (`3af8d92`), so nothing here is hidden from the child; the gate only
 * keeps a young child from wandering in.
 *
 * Kid register only. An adult's own debriefs are on `/history/conversations`,
 * so a typed URL or a stale bookmark is sent there.
 */
export function AccountHistory() {
  const language = langOf(getLocale());
  // `store-and-network`, as `ProgressConversations` has it: `finishStory`'s
  // payload updates no `storyReads` list.
  const data = useLazyLoadQuery<AccountHistoryQuery>(AccountHistoryQuery, {
    language
  }, {
    fetchPolicy: 'store-and-network'
  });
  const [open, setOpen] = useState(false);
  const onOpen = useCallback(() => setOpen(true), []);
  const learner = data.me?.learner;
  const register = registerFor(learner?.defaultCharacter?.audience);
  if (register !== 'kid') return <Navigate to="/history/conversations" replace />;
  if (!open) return <HoldGate onOpen={onOpen} />;
  const now = new Date();
  const week = learner?.familyWeek;
  const talks = week?.talks ?? [];
  // The server's window is eight UTC days; the page shows the viewer's seven.
  const reads = thisWeek(week?.reads ?? [], r => r.finishedAt, now);
  const name = learner?.defaultCharacter?.displayName ?? null;
  const days = weekDays(talks, now);
  const dayName = new Intl.DateTimeFormat(getLocale(), {
    weekday: 'short'
  });
  const counts = topicCounts(talks, now);
  const said = thisWeek(week?.sentences ?? [], p => p.createdAt, now).filter(p => p.sentence).slice(0, SENTENCES_SHOWN);
  return <div className="flex flex-col gap-6">
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body flex flex-col gap-5">
          <div>
            <h2 className="card-title">{t('grown-ups-title')}</h2>
            <p className="text-base-content/60 text-sm">{t('grown-ups-intro')}</p>
          </div>

          <Section title={t('grown-ups-when')}>
            <ol className="flex gap-3">
              {days.map((on, i) => {
              const day = dayName.format(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (WEEK_DAYS - 1 - i)));
              return <li key={i} className="flex flex-col items-center gap-1 text-xs">
                    <span role="img" aria-label={t(on ? 'grown-ups-day-on' : 'grown-ups-day-off', {
                  day
                })} className={`size-4 rounded-full border-2 border-primary ${on ? 'bg-primary' : ''}`} />
                    <span aria-hidden className="text-base-content/60">
                      {day}
                    </span>
                  </li>;
            })}
            </ol>
          </Section>

          <Section title={t('grown-ups-what')}>
            {counts.length === 0 ? <p className="text-base-content/60 text-sm">{t('grown-ups-what-none')}</p> : <ul className="flex flex-col gap-1">
                {counts.map(({
              topic,
              count
            }) => <li key={topic ?? ''} className="flex justify-between gap-3">
                    <span>{topic === null ? t('grown-ups-free-talk') : topicLabel(topic, name)}</span>
                    <span className="text-base-content/60">{t('grown-ups-times', {
                  count
                })}</span>
                  </li>)}
              </ul>}
          </Section>

          <Section title={t('grown-ups-said')}>
            {said.length === 0 ? <p className="text-base-content/60 text-sm">{t('grown-ups-said-none')}</p> : <ul className="flex flex-col gap-3">
                {said.map(p => <li key={p.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-lg">
                      <En>“{p.sentence}”</En>
                    </span>
                    {p.turnId && <HearMyself url={`/audio/${encodeURIComponent(p.turnId)}`} />}
                  </li>)}
              </ul>}
          </Section>

          <Section title={t('grown-ups-books')}>
            <BooksRead reads={reads} language={language} none="grown-ups-books-none" titled={false} />
          </Section>

          <Section title={t('grown-ups-together')}>
            <ul className="list-disc ps-5">
              {suggestions({
              talks,
              reads
            }, now).map((s, i) => <li key={i}>
                  <SuggestionLine s={s} name={name} />
                </li>)}
            </ul>
          </Section>
        </div>
      </div>

      <MemoriesCard memories={learner?.memories ?? []} />

      <div className="card bg-base-100 shadow-xl">
        <div className="card-body flex flex-col gap-3">
          <h2 className="card-title">{t('history-title')}</h2>
          <p className="text-base-content/60 text-sm">{t('history-intro')}</p>
          <SessionList sessions={learner?.sessions?.nodes ?? []} showDebrief />
        </div>
      </div>
    </div>;
}